/**
 * Vue page d'acceptation d'invitation — Phase 6, incrément 6.2.
 *
 * Accessible via la route `/invite/<token>`. L'utilisateur arrive
 * ici depuis le lien email de l'inviteur. Comportement :
 *
 *   1. Au mount, fetch `getInvitationPreview(token)` — peuple
 *      `homeName`, `inviterName`, `role`, `expiresAt`.
 *   2. Affichage selon l'état session :
 *      - Anonyme → CTA « Se connecter pour accepter » +
 *        redirect vers Settings après login (le user reviendra
 *        sur cette URL manuellement — pas de retour automatique
 *        pour rester simple en 6.2).
 *      - Authentifié → bouton « Accepter et rejoindre » qui
 *        appelle `acceptInvitation(token)` puis redirige sur le
 *        nouveau foyer.
 *   3. Erreurs (404, 409, 403) → message i18n + retour `/`.
 *
 * Pas de modal — vue plein-écran centrée, cohérente avec
 * `onboarding/View.ts`.
 */
import { html, type TemplateResult } from 'lit-html';
import styles from './View.module.css';
import { t } from '../../i18n/index.js';
import { ApiError } from '../../api/client.js';
import {
  acceptInvitation,
  getInvitationPreview,
  type ApiInvitationPreview,
} from '../../api/invitations.js';
import { parseInviteToken, navigate } from '../../lib/router.js';
import { getSession } from '../../stores/session.js';
import { switchHome } from '../../stores/homes.js';
import { syncHomesWithServer } from '../../stores/homes-sync.js';
import { showToast } from '../../ui/Toast.js';
import { bumpUi } from '../../stores/ui.js';
import { nestorMark } from '../../ui/NestorMark.js';

type FetchState =
  | { kind: 'loading' }
  | { kind: 'preview'; data: ApiInvitationPreview }
  | { kind: 'error'; messageKey: ErrorKey }
  | { kind: 'accepting' };

type ErrorKey =
  | 'invite_error_expired'
  | 'invite_error_already'
  | 'invite_error_mismatch'
  | 'invite_error_offline';

interface State {
  token: string | null;
  fetch: FetchState;
}

const state: State = {
  token: null,
  fetch: { kind: 'loading' },
};

function setState(patch: Partial<State>): void {
  Object.assign(state, patch);
  bumpUi();
}

/**
 * Mapping `ApiError → ErrorKey` pour les i18n keys. Le serveur
 * renvoie `code` dans le payload error — on l'utilise pour router.
 */
function errorKeyFromApiError(err: ApiError): ErrorKey {
  if (err.code === 'expired_or_invalid') return 'invite_error_expired';
  if (err.code === 'already_accepted') return 'invite_error_already';
  if (err.code === 'email_mismatch') return 'invite_error_mismatch';
  // Status 503 (db_unavailable) ou autre → fallback offline.
  return 'invite_error_offline';
}

async function loadPreview(token: string): Promise<void> {
  setState({ token, fetch: { kind: 'loading' } });
  try {
    const data = await getInvitationPreview(token);
    setState({ fetch: { kind: 'preview', data } });
  } catch (err) {
    if (err instanceof ApiError) {
      setState({ fetch: { kind: 'error', messageKey: errorKeyFromApiError(err) } });
    } else {
      setState({ fetch: { kind: 'error', messageKey: 'invite_error_offline' } });
    }
  }
}

async function onAccept(): Promise<void> {
  if (!state.token) return;
  setState({ fetch: { kind: 'accepting' } });
  try {
    const result = await acceptInvitation(state.token);
    showToast({ message: t('invite_accepted_toast'), kind: 'success' });
    // Re-sync + switch sur le nouveau foyer. La sync ramène la
    // membership fraîche + tout le payload (events, tasks, etc.).
    await syncHomesWithServer();
    switchHome(result.homeId);
    // Bascule sur la vue home appropriée.
    navigate(result.role === 'parent' || result.homeId ? '/' : '/');
  } catch (err) {
    if (err instanceof ApiError) {
      const key = errorKeyFromApiError(err);
      setState({ fetch: { kind: 'error', messageKey: key } });
      showToast({ message: t(key), kind: 'error' });
    } else {
      setState({ fetch: { kind: 'error', messageKey: 'invite_error_offline' } });
    }
  }
}

function previewTemplate(preview: ApiInvitationPreview, isAuthenticated: boolean): TemplateResult {
  const roleKey =
    preview.role === 'parent'
      ? 'invite_role_parent'
      : preview.role === 'teen'
        ? 'invite_role_teen'
        : 'invite_role_child';
  const formattedExpiry = new Date(preview.expiresAt).toLocaleDateString();
  return html`<div class=${styles.card} data-testid="invite-preview">
    <h1 class=${styles.title}>${t('invite_accept_title')} « ${preview.homeName} »</h1>
    <dl class=${styles.meta}>
      ${preview.inviterName
        ? html`<div>
            <dt>${t('invite_accept_invited_by')}</dt>
            <dd>${preview.inviterName}</dd>
          </div>`
        : null}
      <div>
        <dt>${t('invite_accept_role_label')}</dt>
        <dd>${t(roleKey)}</dd>
      </div>
      <div>
        <dt>${t('invite_accept_expires_at')}</dt>
        <dd>${formattedExpiry}</dd>
      </div>
    </dl>
    ${isAuthenticated
      ? html`<button
          type="button"
          class=${styles.primary}
          data-testid="invite-accept-btn"
          @click=${onAccept}
        >
          ${t('invite_accept_button')}
        </button>`
      : html`<p class=${styles.note}>
          ${t('invite_accept_signin_first')}
          <a
            href="#/settings"
            data-testid="invite-signin-link"
            @click=${(e: Event): void => {
              e.preventDefault();
              navigate('/settings');
            }}
            >${t('account_btn_login')}</a
          >
        </p>`}
  </div>`;
}

function errorTemplate(messageKey: ErrorKey): TemplateResult {
  return html`<div class=${styles.card} data-testid="invite-error">
    <h1 class=${styles.title}>${t(messageKey)}</h1>
    <button
      type="button"
      class=${styles.primary}
      data-testid="invite-error-home"
      @click=${(): void => navigate('/')}
    >
      ${t('home')}
    </button>
  </div>`;
}

function loadingTemplate(): TemplateResult {
  return html`<div class=${styles.card} data-testid="invite-loading">
    <p class=${styles.note}>${t('invite_accept_loading')}</p>
  </div>`;
}

/**
 * Rendu de la vue. Côté caller (`AppShell`), on appelle juste
 * `renderInviteView()` quand `route === '/invite'`. Au premier
 * render, on extrait le token de l'URL et on lance le fetch
 * preview.
 */
export function renderInviteView(): TemplateResult {
  // Lazy init au 1er render : extrait le token + déclenche le fetch.
  // Re-render via `bumpUi` met à jour `state.fetch`.
  const token = parseInviteToken();
  if (state.token !== token) {
    if (!token) {
      // URL malformée (`/invite` sans token) → erreur immédiate.
      state.token = null;
      state.fetch = { kind: 'error', messageKey: 'invite_error_expired' };
    } else {
      // Nouveau token → fire-and-forget loadPreview.
      void loadPreview(token);
    }
  }

  const session = getSession();
  const isAuthenticated = session.status === 'authenticated';

  let body: TemplateResult;
  if (state.fetch.kind === 'loading' || state.fetch.kind === 'accepting') {
    body = loadingTemplate();
  } else if (state.fetch.kind === 'error') {
    body = errorTemplate(state.fetch.messageKey);
  } else {
    body = previewTemplate(state.fetch.data, isAuthenticated);
  }

  return html`<section class=${styles.stage} data-testid="invite-view">
    <div class=${styles.brand}>${nestorMark()}</div>
    ${body}
  </section>`;
}

/** @internal pour tests — reset l'état module-level. */
export function _resetInviteViewForTests(): void {
  state.token = null;
  state.fetch = { kind: 'loading' };
}
